import React, { useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { Map } from 'immutable';
import { useSelector } from 'react-redux';
import setDefaultOptions from 'date-fns/setDefaultOptions';
import { nl, enGB } from 'date-fns/locale';
import config from 'react-global-configuration';
import { useTranslation } from 'react-i18next';

import {
  I18nProvider,
  EntityMapperContext,
  Block,
} from '@uncinc/react-drupal-core';
import { SkipNav } from '@uncinc/uncinc-react-kitchen-sink';

import {
  Header,
  AnnualReportHeader,
  BuurtboerderijHeader,
  AmsterdamundergroundHeader,
  Footer,
  BuurtboerderijFooter,
  Debug,
  DomainChooser,
  AmsterdamundergroundFooter,
  VriendenvanderegenboogHeader,
  VriendenvanderegenboogFooter,
} from '../../components';

import blocksMapper from '../../mappers/blocks';
import nodeMapper from '../../mappers/node';
import paragraphMapper from '../../mappers/paragraphs';
import taxonomyMapper from '../../mappers/taxonomy';
import viewsMapper from '../../mappers/views';

import i18next from '../../config/i18next';
import Routes from '../Routes';

import {
  __DEBUG__,
  BLOCK_PINNED_NOTIFICATION_BLOCK_ID,
  DOMAIN_VRIENDEN_REGENBOOG,
  MAIN_CONTENT_ID,
} from '../../config/constants';
import Cookie from '../../components/Cookie';

import './style.scss';
import '../../styles/main.scss';

const LANG_TO_LOCALE = {
  nl: nl,
  en: enGB,
};

const SkipNavWrapper = () => {
  const { t } = useTranslation();
  return (
    <SkipNav skipToContentText={t('skipnav.text')} />
  );
};

const App = () => {
  const path = useSelector((state) => state.getIn(['router', 'location', 'pathname'], ''));
  const entityId = useSelector((state) => state.getIn(['entity', 'entityId'], null));
  const entityType = useSelector((state) => state.getIn(['entity', 'entityType'], null));
  const childEntity = useSelector((state) => state.getIn([entityType, entityId], Map()));
  const rootClasses = useSelector((state) => state.getIn(['ui', 'rootClasses']));
  const language = useSelector((state) => state.getIn(['ui', 'language']));
  const entityExists = useSelector((state) => state.getIn(['entity', 'exists']));
  const currentDomainIsVriendenRegenboog = config.get('CURRENT_DOMAIN') === DOMAIN_VRIENDEN_REGENBOOG;

  const currentEntityId = useSelector((state) => state.getIn(['entity', 'entityId'], null));
  const currentNodeType = useSelector((state) => state.getIn(['node', currentEntityId, 'type', 0, 'target_id'], null));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [path]);

  const classes = classNames('App', rootClasses.join(' '), `theme--${config.get('CURRENT_DOMAIN')}`, {
    'is-front-page': childEntity.get('is_front'),
    'not-found': !entityExists,
  });

  setDefaultOptions({ locale: LANG_TO_LOCALE[language] });

  const entityTypeMapper = useMemo(() => ({
    block: blocksMapper,
    node: nodeMapper,
    paragraph: paragraphMapper,
    taxonomy: taxonomyMapper,
    view: viewsMapper,
  }), []);

  return (
    <I18nProvider locale={language} i18next={i18next}>
      <EntityMapperContext.Provider value={entityTypeMapper}>
        <div className={classes} language={language}>
          <SkipNavWrapper />

          {/* Header */}
          {!currentDomainIsVriendenRegenboog && !(currentNodeType === 'campaign_page_color') && (
            <div>
              <DomainChooser
                domains={{
                  annual_report_regenboog: <AnnualReportHeader />,
                  regenboog: <Block currentLanguage={language} id={BLOCK_PINNED_NOTIFICATION_BLOCK_ID} />,
                }}
              />
            </div>
          )}

          <div className="sticky-container">
            {/* Header */}
            <DomainChooser
              domains={{
                buurtboerderij: <BuurtboerderijHeader />,
                annual_report_regenboog: null,
                amsterdamunderground: <AmsterdamundergroundHeader />,
                vrienden_regenboog: <VriendenvanderegenboogHeader />,
              }}
              fallback={!(currentNodeType === 'campaign_page_color') ? <Header /> : null}
            />

            {/* Main content */}
            <main className="main" id={MAIN_CONTENT_ID}>
              <Routes language={language} />
            </main>
          </div>

          {/* Footer */}
          <DomainChooser
            domains={{
              buurtboerderij: <BuurtboerderijFooter />,
              annual_report_regenboog: null,
              amsterdamunderground: <AmsterdamundergroundFooter />,
              vrienden_regenboog: <VriendenvanderegenboogFooter />,
            }}
            fallback={<Footer />}
          />

          <Cookie />
          {__DEBUG__ && <Debug />}
          <div id="modal-root" />
        </div>
      </EntityMapperContext.Provider>
    </I18nProvider>
  );
};

export default App;
